import PropTypes from 'prop-types';
import React, {useMemo, useRef, useState} from 'react';
import {Button} from 'antd';
import {LeftOutlined, RightOutlined} from '@ant-design/icons';
import {
  StyledInvoicePreviewWithIcon,
  StyledInvoicePreviewTooltipContent,
  StyledInvoicePreviewTooltipBody,
  StyledInvoicePreviewTooltipFooter,
} from './StyledInvoicePreviewWithIcon';
import ImagePreviewTooltip from '../../TransactionsPage/ImagePreviewTooltip';
import InvoiceAttachmentPreview from '../../TransactionsPage/InvoiceAttachmentPreview';
import {useInvoiceAttachmentPreview} from '../../../../hooks/accounting/useInvoiceAttachmentPreview';
import {DocumentApprovedIcon} from '../../../../icons';

const InvoicePreviewWithIcon = ({
  icon,
  invoiceId,
  invoiceIds,
  variant = 'backend',
  ...rest
}) => {
  const ids = useMemo(
    () => invoiceIds || (invoiceId ? [invoiceId] : []),
    [invoiceIds, invoiceId]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const suppressCloseRef = useRef(false);

  const {
    invoicesAttachments,
    previewAttachment,
    showPreview,
  } = useInvoiceAttachmentPreview({variant, handleChangeFullscreen: setIsFullscreen});

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    if (suppressCloseRef.current) return;
    cancelClose();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  const handleTriggerEnter = () => {
    cancelClose();
    setIsOpen(true);
    if (ids[currentIndex]) showPreview(ids[currentIndex]);
  };

  const handleNavigate = (e, newIndex) => {
    e.preventDefault();
    e.stopPropagation();
    cancelClose();
    suppressCloseRef.current = true;
    setCurrentIndex(newIndex);
    if (ids[newIndex]) showPreview(ids[newIndex]);
    setTimeout(() => { suppressCloseRef.current = false; }, 600);
  };

  const onTooltipContentClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  }

  const tooltipContent = (
    <StyledInvoicePreviewTooltipContent
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      onClick={onTooltipContentClick}
    >
      <StyledInvoicePreviewTooltipBody>
        {invoicesAttachments.loading && 'Loading...'}
        {previewAttachment && (
          <InvoiceAttachmentPreview
            attachment={previewAttachment}
            className={invoicesAttachments.loading ? 'd-none' : ''}
            handleChangeFullscreen={setIsFullscreen}
          />
        )}
      </StyledInvoicePreviewTooltipBody>
      {ids.length > 1 && (
        <StyledInvoicePreviewTooltipFooter>
          <Button
            size='small'
            icon={<LeftOutlined />}
            disabled={currentIndex === 0}
            onClick={(e) => handleNavigate(e, currentIndex - 1)}
          />
          <span>{currentIndex + 1} / {ids.length}</span>
          <Button
            size='small'
            icon={<RightOutlined />}
            disabled={currentIndex === ids.length - 1}
            onClick={(e) => handleNavigate(e, currentIndex + 1)}
          />
        </StyledInvoicePreviewTooltipFooter>
      )}
    </StyledInvoicePreviewTooltipContent>
  );

  if (!ids.length) return null;

  return (
    <ImagePreviewTooltip
      classNames={{root: 'image-preview-tooltip-overlay single unset-tooltip-width'}}
      title={tooltipContent}
      open={isOpen}
      trigger={isFullscreen ? 'click' : []}
      onOpenChange={isFullscreen ? (open) => setIsOpen(open) : undefined}
    >
      <StyledInvoicePreviewWithIcon
        {...rest}
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={scheduleClose}
      >
        {icon || <DocumentApprovedIcon />}
      </StyledInvoicePreviewWithIcon>
    </ImagePreviewTooltip>
  );
}

InvoicePreviewWithIcon.propTypes = {
  invoiceId: PropTypes.string,
  invoiceIds: PropTypes.arrayOf(PropTypes.string),
  variant: PropTypes.oneOf(['accounting', 'backend']),
}

export default InvoicePreviewWithIcon;
